<script setup lang="ts">
import type {
  ContractGenerationTemplateRoleShareInDb,
  ContractRoleShareInDb,
  ContractualPartyRoleShareInDb,
  PermissionRoleCreate,
  PermissionRoleInDb,
  RoleUsersResult,
  UserInDb,
} from '@/api';
import {
  NButton,
  NDropdown,
  NIcon,
  useDialog,
  type DataTableColumns,
  type DropdownOption,
} from 'naive-ui';
import { computed, h, onMounted, ref, watch, type Component, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  IconDelete,
  IconDocumentText,
  IconDocumentWord,
  IconEdit,
  IconMore,
  IconPeople,
  IconUserAdd,
  IconUserRemove,
} from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaSelectionBar from '@/components/design/composite/GaiaSelectionBar.vue';
import VaultSharesTable from '@/components/file_vault/VaultSharesTable.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaUserPickerList from '@/components/design/domain/people/GaiaUserPickerList.vue';
import type { GaiaPickerListInstance } from '@/components/design/domain/people/types';
import RoleFormDialog from '@/components/permissions/RoleFormDialog.vue';
import { roleFlagGroups } from '@/components/permissions/roleFlags';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { ContractShareService } from '@/services/contractShareService';
import { PermissionsService, isAdminRole } from '@/services/permissionsService';
import { TemplateShareService } from '@/services/templateShareService';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import { useAuthStore } from '@/stores/auth';
import { ACCESS_LEVEL_OPTIONS, formatAccessPermissions } from '@/utils/accessPermission';
import { logger } from '@/utils/logger';
import { isAccessDenied } from '@/utils/errorHandler';
import {
  contractRoute as contractDetailRoute,
  contractualPartyRoute,
  templateRoute as templateDetailRoute,
} from '@/router/links';

/**
 * One role: who has it, what it allows, and what has been shared with it.
 * Each of those is a tab, so the page shows one list at a time instead of
 * three stacked tables; the tab and the members page live in the URL.
 */

type RoleTab = 'members' | 'permissions' | 'contracts' | 'parties' | 'files' | 'templates';

const route = useRoute();
const router = useRouter();
const roleUid = computed(() => route.params.uid as string);
const authStore = useAuthStore();
const message = useGaiaToast();
const dialog = useDialog();

const isAdmin = computed(() => authStore.userPermissions.is_org_admin);

const {
  activeTab,
  currentPage: membersPage,
  pageSize: membersPageSize,
  searchQuery: membersSearch,
} = useListQuerySync({
  defaultTab: 'members',
  allowedTabs: ['members', 'permissions', 'contracts', 'parties', 'files', 'templates'],
});

const role = ref<PermissionRoleInDb | null>(null);
const loading = ref(false);

// ==================== Role ====================

async function fetchRole(): Promise<void> {
  loading.value = true;
  try {
    role.value = await PermissionsService.getRole(roleUid.value);
  } catch (error) {
    // A refused role renders the page's "Role not found" state; no toast on top.
    if (isAccessDenied(error)) {
      logger.warn('Role not available:', error);
    } else {
      message.error(error instanceof Error ? error.message : 'Failed to load role');
    }
    role.value = null;
  } finally {
    loading.value = false;
  }
}

const showEditModal = ref(false);
const saving = ref(false);
const saveError = ref<string | null>(null);

function openEditModal(): void {
  if (!isAdmin.value || !role.value) return;
  saveError.value = null;
  showEditModal.value = true;
}

async function handleSaveRole(payload: PermissionRoleCreate): Promise<void> {
  if (!isAdmin.value || !role.value) return;
  saving.value = true;
  saveError.value = null;
  try {
    await PermissionsService.updateRole(role.value.uid, payload);
    message.success('Role updated');
    showEditModal.value = false;
    await fetchRole();
  } catch (error) {
    saveError.value = error instanceof Error ? error.message : 'Failed to update role';
  } finally {
    saving.value = false;
  }
}

function menuIcon(icon: Component): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

/** Protected roles can't be deleted, so they get no menu at all. */
const roleMenuOptions = computed((): DropdownOption[] =>
  role.value && !role.value.protected
    ? [{ key: 'delete', label: 'Delete role', icon: menuIcon(IconDelete) }]
    : []
);

function onRoleMenu(key: string | number): void {
  if (key !== 'delete' || !role.value) return;
  const current = role.value;
  dialog.warning({
    title: `Delete ${current.name}?`,
    content:
      'Everyone with this role loses what it allows, and anything shared with it stops being shared. This can’t be undone.',
    positiveText: 'Delete role',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await PermissionsService.deleteRole(current.uid);
        message.success('Role deleted');
        await router.push({ name: 'settings-permissions' });
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to delete role');
      }
    },
  });
}

// ==================== Members (server-paged) ====================

const members = ref<UserInDb[]>([]);
const membersTotal = ref(0);
const membersLoading = ref(false);
const selectedMemberUids = ref<string[]>([]);
const removingMembers = ref(false);

async function fetchMembers(): Promise<void> {
  membersLoading.value = true;
  try {
    const result = await PermissionsService.searchUsersInRole(roleUid.value, {
      text: membersSearch.value.trim(),
      page: membersPage.value,
      pageSize: membersPageSize.value,
    });
    // Removing the last member(s) on the last page leaves it empty: step back
    // a page, and the page watcher fetches it.
    if (result.results.length === 0 && membersPage.value > 1 && result.result_count > 0) {
      membersPage.value = Math.ceil(result.result_count / membersPageSize.value);
      return;
    }
    members.value = result.results;
    membersTotal.value = result.result_count;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load members');
    members.value = [];
    membersTotal.value = 0;
  } finally {
    membersLoading.value = false;
  }
}

const membersPagination = usePaginationConfig(membersPage, membersPageSize, membersTotal);

// usePaginationConfig only updates the refs; the fetch follows them.
watch([membersPage, membersPageSize], () => {
  void fetchMembers();
});

function applyMembersSearch(): void {
  if (membersPage.value === 1) {
    void fetchMembers();
  } else {
    membersPage.value = 1;
  }
}

function people(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

/** "Removed 3 people", or what failed — shared by adding and removing. */
function reportRoleUsers(result: RoleUsersResult, verb: 'Added' | 'Removed'): void {
  const failed = result.results.filter(item => !item.success);
  if (failed.length === 0) {
    message.success(`${verb} ${people(result.successful)}`);
    return;
  }
  message.warning(
    `${verb} ${people(result.successful)}. ${failed.length} failed: ${failed
      .map(item => item.error)
      .join(', ')}`
  );
}

function confirmRemoveMembers(uids: string[], label: string): void {
  if (!isAdmin.value || uids.length === 0) return;
  dialog.warning({
    title: `Remove ${label} from ${role.value?.name ?? 'this role'}?`,
    content: 'They stay in the organisation but lose what this role allows.',
    positiveText: 'Remove',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      removingMembers.value = true;
      try {
        const result = await PermissionsService.removeUsersFromRole(roleUid.value, uids);
        reportRoleUsers(result, 'Removed');
        selectedMemberUids.value = selectedMemberUids.value.filter(uid => !uids.includes(uid));
        await fetchMembers();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to remove members');
      } finally {
        removingMembers.value = false;
      }
    },
  });
}

// ==================== Add members ====================

const showAddMembersModal = ref(false);
const addMembersListRef = ref<GaiaPickerListInstance | null>(null);
const pickedUserUids = ref<string[]>([]);
const assigning = ref(false);
/**
 * Already-assigned users are shown disabled rather than filtered out: hiding
 * them made a search for someone already in the role look like they did not
 * exist. The table only holds one page of members, so the picker gets the full
 * set, fetched when it opens.
 */
const memberUids = ref<string[]>([]);

function openAddMembersModal(): void {
  if (!isAdmin.value) return;
  pickedUserUids.value = [];
  showAddMembersModal.value = true;
  addMembersListRef.value?.reset();
  memberUids.value = [];
  void PermissionsService.listAllUserUidsInRole(roleUid.value)
    .then(uids => {
      memberUids.value = uids;
    })
    .catch((error: unknown) => {
      // Cosmetic: without it members just aren't greyed out, and the backend
      // reports a duplicate assignment per user.
      logger.error('Failed to load the users already in the role:', error);
    });
}

async function handleAddMembers(): Promise<void> {
  if (!isAdmin.value || pickedUserUids.value.length === 0) return;
  assigning.value = true;
  try {
    const result = await PermissionsService.assignUsersToRole(roleUid.value, pickedUserUids.value);
    reportRoleUsers(result, 'Added');
    showAddMembersModal.value = false;
    await fetchMembers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add members');
  } finally {
    assigning.value = false;
  }
}

// ==================== Shared contracts & templates ====================

const sharedContracts = ref<ContractRoleShareInDb[]>([]);
const loadingContracts = ref(false);
const sharedTemplates = ref<ContractGenerationTemplateRoleShareInDb[]>([]);
const loadingTemplates = ref(false);
const sharedParties = ref<ContractualPartyRoleShareInDb[]>([]);
const loadingParties = ref(false);

async function fetchSharedParties(): Promise<void> {
  loadingParties.value = true;
  try {
    sharedParties.value = await ContractualPartyShareService.getPartySharesByRole(roleUid.value);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load parties shared with this role'
    );
    sharedParties.value = [];
  } finally {
    loadingParties.value = false;
  }
}

async function fetchSharedContracts(): Promise<void> {
  loadingContracts.value = true;
  try {
    sharedContracts.value = await ContractShareService.getContractSharesByRole(roleUid.value);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load contracts shared with this role'
    );
    sharedContracts.value = [];
  } finally {
    loadingContracts.value = false;
  }
}

async function fetchSharedTemplates(): Promise<void> {
  loadingTemplates.value = true;
  try {
    sharedTemplates.value = await TemplateShareService.getTemplateSharesByRole(roleUid.value);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load templates shared with this role'
    );
    sharedTemplates.value = [];
  } finally {
    loadingTemplates.value = false;
  }
}

/** One share, contract or template — the two behave identically here. */
interface ShareTarget {
  kind: 'contract' | 'template' | 'party';
  uid: string;
  name: string;
  permissions: number;
}

const SHARE_SERVICE = {
  contract: {
    update: (uid: string, bitmask: number): Promise<unknown> =>
      ContractShareService.updateRoleSharePermissions(uid, roleUid.value, bitmask),
    remove: (uid: string): Promise<unknown> =>
      ContractShareService.removeRoleShare(uid, roleUid.value),
    refresh: fetchSharedContracts,
  },
  template: {
    update: (uid: string, bitmask: number): Promise<unknown> =>
      TemplateShareService.updateRoleSharePermissions(uid, roleUid.value, bitmask),
    remove: (uid: string): Promise<unknown> =>
      TemplateShareService.removeRoleShare(uid, roleUid.value),
    refresh: fetchSharedTemplates,
  },
  party: {
    update: (uid: string, bitmask: number): Promise<unknown> =>
      ContractualPartyShareService.updateRoleSharePermissions(uid, roleUid.value, bitmask),
    remove: (uid: string): Promise<unknown> =>
      ContractualPartyShareService.removeRoleShare(uid, roleUid.value),
    refresh: fetchSharedParties,
  },
};

const editingShare = ref<ShareTarget | null>(null);
const editShareBitmask = ref(0);
const savingShare = ref(false);

function openEditShare(target: ShareTarget): void {
  editingShare.value = target;
  editShareBitmask.value = target.permissions;
}

async function saveShare(): Promise<void> {
  const target = editingShare.value;
  if (!target || editShareBitmask.value === 0 || savingShare.value) return;
  savingShare.value = true;
  try {
    await SHARE_SERVICE[target.kind].update(target.uid, editShareBitmask.value);
    message.success('Access updated');
    editingShare.value = null;
    await SHARE_SERVICE[target.kind].refresh();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update access');
  } finally {
    savingShare.value = false;
  }
}

function confirmRemoveShare(target: ShareTarget): void {
  dialog.warning({
    title: `Stop sharing ${target.name}?`,
    content: `People with this role lose the access the role gave them to this ${target.kind}. Access they have in other ways stays.`,
    positiveText: 'Stop sharing',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await SHARE_SERVICE[target.kind].remove(target.uid);
        message.success(`${target.name} is no longer shared with this role`);
        await SHARE_SERVICE[target.kind].refresh();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to stop sharing');
      }
    },
  });
}

// ==================== Tabs ====================

/**
 * Contracts and templates only get a tab when the organisation has the feature
 * — or when something is shared anyway, so nothing already shared is hidden.
 */
/** Reported by the Files & folders table once it has loaded. */
const sharedVaultItemCount = ref<number | null>(null);

const tabs = computed((): GaiaTabBarItem[] => {
  const items: GaiaTabBarItem[] = [
    { key: 'members', label: 'Members', count: membersTotal.value },
    { key: 'permissions', label: 'Permissions' },
  ];
  items.push(
    { key: 'contracts', label: 'Contracts', count: sharedContracts.value.length },
    { key: 'parties', label: 'Parties', count: sharedParties.value.length },
    { key: 'files', label: 'Files & folders', count: sharedVaultItemCount.value ?? undefined }
  );
  if (authStore.userPermissions.can_use_contract_generation || sharedTemplates.value.length > 0) {
    items.push({ key: 'templates', label: 'Templates', count: sharedTemplates.value.length });
  }
  return items;
});

const currentTab = computed(
  (): RoleTab =>
    (tabs.value.some(tab => tab.key === activeTab.value) ? activeTab.value : 'members') as RoleTab
);

// ==================== Permissions tab ====================

const flagsLocked = computed(() => role.value !== null && isAdminRole(role.value));
const flagGroups = computed(() => roleFlagGroups(authStore.userPermissions));

// ==================== Table cells ====================

/** A row's "⋯" menu. Clicks stop here so the row doesn't also open. */
function renderRowMenu(
  options: DropdownOption[],
  onSelect: (key: string) => void,
  label: string
): VNode {
  return h(
    'span',
    {
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
      },
    },
    [
      h(
        NDropdown,
        {
          trigger: 'click',
          placement: 'bottom-end',
          options,
          onSelect: (key: string | number) => {
            onSelect(String(key));
          },
        },
        {
          default: () =>
            h(
              NButton,
              { quaternary: true, size: 'small', 'aria-label': label, title: label },
              { icon: () => h(NIcon, null, { default: () => h(IconMore) }) }
            ),
        }
      ),
    ]
  );
}

/** A bold first line with an optional muted second one. */
/** The primary line links to what the row lists, so cmd- and middle-click open it in a new tab. */
function renderTitle(to: RouteLocationRaw, primary: string, secondary?: string | null): VNode {
  return h('div', { style: { display: 'flex', flexDirection: 'column', minWidth: 0 } }, [
    h(
      RouterLink,
      {
        to,
        style: 'color: inherit; text-decoration: none',
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
        },
      },
      {
        default: () =>
          h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => primary),
      }
    ),
    secondary ? h(GaiaText, { size: 'control', tone: 'subtle' }, () => secondary) : null,
  ]);
}

function renderAccess(bitmask: number): VNode {
  return h(GaiaTag, { variant: 'muted', size: 'small' }, () => formatAccessPermissions(bitmask));
}

const SHARE_MENU: DropdownOption[] = [
  { key: 'access', label: 'Change access', icon: menuIcon(IconEdit) },
  { type: 'divider', key: 'divider' },
  { key: 'remove', label: 'Stop sharing', icon: menuIcon(IconDelete) },
];

function onShareMenu(target: ShareTarget, key: string): void {
  if (key === 'access') openEditShare(target);
  if (key === 'remove') confirmRemoveShare(target);
}

function displayName(user: UserInDb): string {
  return user.full_name || user.email;
}

const memberColumns = computed((): DataTableColumns<UserInDb> => [
  ...(isAdmin.value ? [{ type: 'selection' as const, width: 44 }] : []),
  {
    title: 'Member',
    key: 'email',
    render: row =>
      renderTitle(memberRoute(row), displayName(row), row.full_name ? row.email : null),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            [{ key: 'remove', label: 'Remove from role', icon: menuIcon(IconUserRemove) }],
            () => {
              confirmRemoveMembers([row.uid], displayName(row));
            },
            `Actions for ${row.email}`
          )
        : h('span'),
  },
]);

const contractColumns = computed((): DataTableColumns<ContractRoleShareInDb> => [
  {
    title: 'Contract',
    key: 'contract',
    render: row =>
      renderTitle(
        contractRoute(row),
        row.contract.name || 'Untitled contract',
        row.contract.contract_type?.name
      ),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row => renderAccess(row.permissions),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            SHARE_MENU,
            key => {
              onShareMenu(
                {
                  kind: 'contract',
                  uid: row.contract.uid,
                  name: row.contract.name || 'this contract',
                  permissions: row.permissions,
                },
                key
              );
            },
            `Actions for ${row.contract.name}`
          )
        : h('span'),
  },
]);

const partyColumns = computed((): DataTableColumns<ContractualPartyRoleShareInDb> => [
  {
    title: 'Party',
    key: 'contractual_party',
    render: row =>
      renderTitle(contractualPartyRoute(row.contractual_party.uid), row.contractual_party.name),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row => renderAccess(row.permissions),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            SHARE_MENU,
            key => {
              onShareMenu(
                {
                  kind: 'party',
                  uid: row.contractual_party.uid,
                  name: row.contractual_party.name,
                  permissions: row.permissions,
                },
                key
              );
            },
            `Actions for ${row.contractual_party.name}`
          )
        : h('span'),
  },
]);

const templateColumns = computed((): DataTableColumns<ContractGenerationTemplateRoleShareInDb> => [
  {
    title: 'Template',
    key: 'template',
    render: row =>
      renderTitle(
        templateRoute(row),
        row.template.name || 'Untitled template',
        row.template.description
      ),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row => renderAccess(row.permissions),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            SHARE_MENU,
            key => {
              onShareMenu(
                {
                  kind: 'template',
                  uid: row.template.uid,
                  name: row.template.name || 'this template',
                  permissions: row.permissions,
                },
                key
              );
            },
            `Actions for ${row.template.name}`
          )
        : h('span'),
  },
]);

/** Rows open what they list. */
function linkRow(to: RouteLocationRaw): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(to);
    },
  };
}

function memberRoute(row: UserInDb): RouteLocationRaw {
  return { name: 'user-detail', params: { uid: row.uid } };
}

function contractRoute(row: ContractRoleShareInDb): RouteLocationRaw {
  return contractDetailRoute(row.contract.uid);
}

function templateRoute(row: ContractGenerationTemplateRoleShareInDb): RouteLocationRaw {
  return templateDetailRoute(row.template.uid);
}

function memberRowProps(row: UserInDb): Record<string, unknown> {
  return linkRow(memberRoute(row));
}

function contractRowProps(row: ContractRoleShareInDb): Record<string, unknown> {
  return linkRow(contractRoute(row));
}

function partyRowProps(row: ContractualPartyRoleShareInDb): Record<string, unknown> {
  return linkRow(contractualPartyRoute(row.contractual_party.uid));
}

function templateRowProps(row: ContractGenerationTemplateRoleShareInDb): Record<string, unknown> {
  return linkRow(templateRoute(row));
}

// ==================== Lifecycle ====================

async function loadAll(): Promise<void> {
  // The role first: members and shares hang off it.
  await fetchRole();
  if (!role.value) return;
  await Promise.all([
    fetchMembers(),
    fetchSharedContracts(),
    fetchSharedParties(),
    fetchSharedTemplates(),
  ]);
}

onMounted(() => {
  void loadAll();
});

watch(roleUid, uid => {
  if (!uid) return;
  selectedMemberUids.value = [];
  membersSearch.value = '';
  if (membersPage.value === 1) {
    void loadAll();
  } else {
    // The page watcher fetches the members; the rest load here.
    membersPage.value = 1;
    void Promise.all([
      fetchRole(),
      fetchSharedContracts(),
      fetchSharedParties(),
      fetchSharedTemplates(),
    ]);
  }
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'settings-permissions' }">{{ navLabels.people }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ role?.name ?? 'Role' }}</n-breadcrumb-item>
    </template>

    <n-spin :show="loading && !role">
      <template v-if="role">
        <!-- Header: what the role is, and what you do to it -->
        <n-flex vertical :size="16" :wrap="false" :style="{ marginBottom: '20px' }">
          <n-flex justify="space-between" align="flex-start" :size="16" :wrap="false">
            <n-flex vertical :size="6" :style="{ minWidth: 0, flex: '1 1 auto' }">
              <n-flex align="center" :size="10" :wrap="false">
                <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
                  {{ role.name }}
                </gaia-header>
                <gaia-tag v-if="role.protected" variant="muted" size="small">Built-in</gaia-tag>
              </n-flex>
              <gaia-text v-if="role.description" size="body" tone="subtle">
                {{ role.description }}
              </gaia-text>
            </n-flex>
            <n-flex v-if="isAdmin" align="center" :size="8" :wrap="false" :style="{ flex: 'none' }">
              <gaia-button @click="openEditModal">
                <template #icon>
                  <n-icon :component="IconEdit" />
                </template>
                Edit role
              </gaia-button>
              <n-dropdown
                v-if="roleMenuOptions.length"
                trigger="click"
                placement="bottom-end"
                :options="roleMenuOptions"
                @select="onRoleMenu"
              >
                <gaia-button aria-label="More actions" title="More actions">
                  <template #icon>
                    <n-icon :component="IconMore" />
                  </template>
                </gaia-button>
              </n-dropdown>
            </n-flex>
          </n-flex>
          <gaia-tab-bar
            :items="tabs"
            :active="currentTab"
            @select="(key: string) => (activeTab = key)"
          />
        </n-flex>

        <!-- Members -->
        <template v-if="currentTab === 'members'">
          <gaia-empty-state
            v-if="membersTotal === 0 && !membersSearch.trim() && !membersLoading"
            title="No one has this role yet"
            dashed
          >
            <template #icon>
              <n-icon :component="IconPeople" :size="18" />
            </template>
            Add members to give them what this role allows.
            <template v-if="isAdmin" #actions>
              <gaia-button type="primary" @click="openAddMembersModal">Add members</gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else>
            <n-flex
              justify="space-between"
              align="center"
              :size="12"
              :style="{ marginBottom: '12px' }"
            >
              <gaia-search-input
                v-model:value="membersSearch"
                placeholder="Search members"
                :debounce="500"
                style="width: 260px"
                @search="applyMembersSearch"
              />
              <gaia-button v-if="isAdmin" type="primary" @click="openAddMembersModal">
                <template #icon>
                  <n-icon :component="IconUserAdd" />
                </template>
                Add members
              </gaia-button>
            </n-flex>

            <gaia-selection-bar
              v-if="selectedMemberUids.length > 0"
              :label="`${selectedMemberUids.length} selected`"
              :style="{ marginBottom: '12px' }"
              @clear="selectedMemberUids = []"
            >
              <gaia-button
                size="small"
                type="error"
                ghost
                :loading="removingMembers"
                @click="confirmRemoveMembers(selectedMemberUids, people(selectedMemberUids.length))"
              >
                Remove from role
              </gaia-button>
            </gaia-selection-bar>

            <gaia-table
              :columns="memberColumns"
              :data="members"
              :loading="membersLoading"
              remote
              :pagination="membersPagination"
              :row-key="(row: UserInDb) => row.uid"
              :checked-row-keys="selectedMemberUids"
              :row-props="memberRowProps"
              @update:checked-row-keys="
                (keys: (string | number)[]) => (selectedMemberUids = keys.map(String))
              "
            >
              <template #empty>
                <n-empty
                  v-if="!membersLoading"
                  :description="`No members match “${membersSearch.trim()}”`"
                />
              </template>
            </gaia-table>
          </template>
        </template>

        <!-- Permissions -->
        <template v-else-if="currentTab === 'permissions'">
          <gaia-banner v-if="flagsLocked" type="info">
            Admins can do everything in the organisation, so this role has no permissions to switch
            on or off.
          </gaia-banner>
          <gaia-card v-else>
            <template #header>What people with this role can do</template>
            <template v-if="isAdmin" #header-extra>
              <gaia-button size="small" @click="openEditModal">
                <template #icon>
                  <n-icon :component="IconEdit" />
                </template>
                Edit permissions
              </gaia-button>
            </template>
            <n-flex vertical :size="18" :wrap="false">
              <n-flex
                v-for="group in flagGroups"
                :key="group.title"
                vertical
                :size="4"
                :wrap="false"
              >
                <gaia-text variant="label" size="micro" tone="faint">{{ group.title }}</gaia-text>
                <n-flex
                  v-for="flag in group.flags"
                  :key="flag.key"
                  justify="space-between"
                  align="center"
                  :size="16"
                  :wrap="false"
                  :style="{ padding: '8px 0' }"
                >
                  <n-flex vertical :size="2" :style="{ minWidth: 0 }">
                    <gaia-text size="body" weight="semibold" tone="ink">{{ flag.label }}</gaia-text>
                    <gaia-text size="control" tone="subtle">{{ flag.hint }}</gaia-text>
                  </n-flex>
                  <gaia-status-chip
                    v-if="flag.kind === 'toggle'"
                    :tone="role[flag.key] ? 'success' : 'neutral'"
                    size="small"
                  >
                    {{ role[flag.key] ? 'Allowed' : 'Not allowed' }}
                  </gaia-status-chip>
                  <gaia-status-chip
                    v-else
                    :tone="role[flag.key] ? 'success' : 'neutral'"
                    size="small"
                  >
                    {{ formatAccessPermissions(role[flag.key]) }}
                  </gaia-status-chip>
                </n-flex>
              </n-flex>
            </n-flex>
          </gaia-card>
        </template>

        <!-- Shared contracts -->
        <template v-else-if="currentTab === 'contracts'">
          <gaia-empty-state
            v-if="sharedContracts.length === 0 && !loadingContracts"
            title="No contracts shared with this role"
            dashed
          >
            <template #icon>
              <n-icon :component="IconDocumentText" :size="18" />
            </template>
            Share a contract from its page to give everyone with this role access to it.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Everyone with this role can open these contracts with the access shown.
            </gaia-text>
            <gaia-table
              :columns="contractColumns"
              :data="sharedContracts"
              :loading="loadingContracts"
              :row-key="(row: ContractRoleShareInDb) => row.contract.uid"
              :row-props="contractRowProps"
            />
          </template>
        </template>

        <!-- Shared contractual parties -->
        <template v-else-if="currentTab === 'parties'">
          <gaia-empty-state
            v-if="sharedParties.length === 0 && !loadingParties"
            title="No parties shared with this role"
            dashed
          >
            <template #icon>
              <n-icon :component="IconPeople" :size="18" />
            </template>
            Share a contractual party from its page to give everyone with this role access to it.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Everyone with this role can open these parties with the access shown.
            </gaia-text>
            <gaia-table
              :columns="partyColumns"
              :data="sharedParties"
              :loading="loadingParties"
              :row-key="(row: ContractualPartyRoleShareInDb) => row.contractual_party.uid"
              :row-props="partyRowProps"
            />
          </template>
        </template>

        <!-- Shared templates -->
        <template v-else-if="currentTab === 'templates'">
          <gaia-empty-state
            v-if="sharedTemplates.length === 0 && !loadingTemplates"
            title="No templates shared with this role"
            dashed
          >
            <template #icon>
              <n-icon :component="IconDocumentWord" :size="18" />
            </template>
            Share a template from its page to let everyone with this role use it.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Everyone with this role can use these templates with the access shown.
            </gaia-text>
            <gaia-table
              :columns="templateColumns"
              :data="sharedTemplates"
              :loading="loadingTemplates"
              :row-key="(row: ContractGenerationTemplateRoleShareInDb) => row.template.uid"
              :row-props="templateRowProps"
            />
          </template>
        </template>

        <!-- Shared vault folders and files. Kept mounted (v-show) so the tab
             shows its count before it is opened. -->
        <div v-show="currentTab === 'files'">
          <vault-shares-table
            :subject="{ kind: 'role', uid: roleUid }"
            :subject-name="role?.name ?? 'this role'"
            :manageable="isAdmin"
            @count="(count: number) => (sharedVaultItemCount = count)"
          />
        </div>
      </template>

      <gaia-empty-state v-else-if="!loading" title="Role not found">
        It may have been deleted, or you don’t have access to it.
        <template #actions>
          <gaia-button @click="router.push({ name: 'settings-permissions' })">
            Back to roles
          </gaia-button>
        </template>
      </gaia-empty-state>
    </n-spin>

    <role-form-dialog
      v-model:show="showEditModal"
      :role="role"
      :loading="saving"
      :error="saveError ?? undefined"
      @submit="handleSaveRole"
    />

    <!-- Add members -->
    <gaia-modal
      v-model:show="showAddMembersModal"
      width="640px"
      :title="`Add members to ${role?.name ?? 'this role'}`"
      :mask-closable="!assigning"
      :close-on-esc="!assigning"
    >
      <gaia-user-picker-list
        ref="addMembersListRef"
        v-model:value="pickedUserUids"
        multiple
        :page-size="10"
        :disabled-uids="memberUids"
      />
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="assigning" @click="showAddMembersModal = false">
            Cancel
          </gaia-button>
          <gaia-button
            type="primary"
            :loading="assigning"
            :disabled="pickedUserUids.length === 0"
            @click="handleAddMembers"
          >
            {{
              pickedUserUids.length > 0
                ? `Add ${pickedUserUids.length} ${pickedUserUids.length === 1 ? 'member' : 'members'}`
                : 'Add members'
            }}
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Change a share's access -->
    <gaia-modal
      :show="editingShare !== null"
      width="400px"
      title="Change access"
      :mask-closable="!savingShare"
      :close-on-esc="!savingShare"
      @update:show="(value: boolean) => !value && (editingShare = null)"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-text size="body" tone="subtle" :style="{ lineHeight: 1.55 }">
          What everyone with {{ role?.name ?? 'this role' }} can do with {{ editingShare?.name }}.
        </gaia-text>
        <gaia-select
          v-model:value="editShareBitmask"
          :options="ACCESS_LEVEL_OPTIONS"
          placeholder="Choose access"
          style="width: 100%"
        />
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="savingShare" @click="editingShare = null">Cancel</gaia-button>
          <gaia-button
            type="primary"
            :loading="savingShare"
            :disabled="editShareBitmask === 0 || editShareBitmask === editingShare?.permissions"
            @click="saveShare"
          >
            Save access
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>
